/**
 * Canvas renderer. Reads GameState, writes pixels. Never mutates state.
 *
 * Layered draws, painter's algorithm:
 *   1. Clear canvas
 *   2. Maze tiles (walls + corridor floor)
 *   3. Player car
 *
 * Later phases add: flags, smoke, enemies, HUD, radar.
 */

import type { FlagKind, GameState } from '../sim/types.ts';
import { type Maze, TILE } from '../sim/maze.ts';
import { TILE_PX, type Viewport, computeViewport, subpixelToPixel, tileToPixel } from './viewport.ts';

import { drawHud } from './hud.ts';
import { drawRadar } from './radar.ts';
import { LAYOUT } from './layout.ts';
import { drawSprite, type SpriteName, type SpriteSheet } from './sprites.ts';

export type RenderInputs = {
  readonly spriteSheet?: SpriteSheet | undefined;
  readonly hudLogo?: CanvasImageSource | undefined;
  readonly highScore: number;
};

export const NO_RENDER_INPUTS: RenderInputs = { highScore: 0 };

const COLOR = {
  bg: '#06060c',
  wall: '#1a2742',
  wallEdge: '#293a64',
  corridor: '#0d1224',
  corridorGrid: '#11182f',
  player: '#7aa2f7',
  playerOutline: '#cdd6f4',
  flagYellow: '#f7c873',
  flagPostYellow: '#94a3b8',
  flagDoubler: '#f78fb3',
  flagDoublerPost: '#cdd6f4',
  flagFuel: '#a6e3a1',
  flagFuelPost: '#cdd6f4',
  flagBoost: '#fab387',
  flagBoostPost: '#cdd6f4',
  enemy: '#f38ba8',
  enemyOutline: '#fab387',
  enemyStunned: '#6c7086',
  smoke: 'rgba(180, 180, 200, 0.35)',
  smokeFresh: 'rgba(220, 220, 240, 0.55)',
  boulder: '#3f4663',
  boulderShadow: '#1a1d2c',
} as const;

function flagColors(kind: FlagKind): { pennant: string; post: string; glyph: string } {
  switch (kind) {
    case 'doubler':
      return { pennant: COLOR.flagDoubler, post: COLOR.flagDoublerPost, glyph: '×2' };
    case 'fuel_bonus':
      return { pennant: COLOR.flagFuel, post: COLOR.flagFuelPost, glyph: 'F' };
    case 'speed_boost':
      return { pennant: COLOR.flagBoost, post: COLOR.flagBoostPost, glyph: '»' };
    case 'yellow':
    default:
      return { pennant: COLOR.flagYellow, post: COLOR.flagPostYellow, glyph: '' };
  }
}

export function render(ctx: CanvasRenderingContext2D, state: GameState, ri: RenderInputs = NO_RENDER_INPUTS): void {
  const viewport = computeViewport(
    state.player.x,
    state.player.y,
    state.maze.width,
    state.maze.height,
    LAYOUT.playW,
    LAYOUT.playH,
  );

  ctx.fillStyle = COLOR.bg;
  ctx.fillRect(0, 0, LAYOUT.canvasW, LAYOUT.canvasH);

  // Clip world rendering to the play area so cars/flags near the right edge
  // don't bleed into the sidebar.
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, LAYOUT.playW, LAYOUT.playH);
  ctx.clip();
  drawMaze(ctx, state.maze, viewport, ri.spriteSheet);
  drawBoulders(ctx, state, viewport, ri.spriteSheet);
  drawSmokes(ctx, state, viewport, ri.spriteSheet);
  drawFlags(ctx, state, viewport, ri.spriteSheet);
  drawEnemies(ctx, state, viewport, ri.spriteSheet);
  drawPlayer(ctx, state, viewport, ri.spriteSheet);
  ctx.restore();

  drawHud(ctx, state, { highScore: ri.highScore, logo: ri.hudLogo });

  // Radar minimap lives at the bottom of the sidebar.
  const radarPad = 12;
  const radarSize = LAYOUT.sidebarW - radarPad * 2;
  drawRadar(ctx, state, {
    x: LAYOUT.sidebarX + radarPad,
    y: LAYOUT.sidebarH - radarSize - radarPad,
    width: radarSize,
    height: radarSize,
  });
}

function drawBoulders(ctx: CanvasRenderingContext2D, state: GameState, vp: Viewport, sprites?: SpriteSheet): void {
  for (const b of state.maze.boulders) {
    const cx = tileToPixel(b.x) - vp.x;
    const cy = tileToPixel(b.y) - vp.y;
    if (cx < -TILE_PX || cy < -TILE_PX || cx > vp.width || cy > vp.height) continue;
    if (sprites) {
      drawSprite(ctx, sprites, 'blocked_road', cx, cy);
      continue;
    }
    const pad = 2;
    ctx.fillStyle = COLOR.boulderShadow;
    ctx.fillRect(cx + pad, cy + pad + 1, TILE_PX - pad * 2, TILE_PX - pad * 2);
    ctx.fillStyle = COLOR.boulder;
    ctx.fillRect(cx + pad, cy + pad, TILE_PX - pad * 2, TILE_PX - pad * 2);
  }
}

function drawSmokes(ctx: CanvasRenderingContext2D, state: GameState, vp: Viewport, sprites?: SpriteSheet): void {
  for (const smoke of state.smokes) {
    const cx = tileToPixel(smoke.tile.x) - vp.x;
    const cy = tileToPixel(smoke.tile.y) - vp.y;
    if (cx < -TILE_PX || cy < -TILE_PX || cx > vp.width || cy > vp.height) continue;
    const age = state.tick - smoke.bornTick;
    if (sprites) {
      ctx.globalAlpha = age < state.config.smokeTtl / 3 ? 0.95 : 0.72;
      drawSprite(ctx, sprites, 'smoke_screen', cx, cy);
      ctx.globalAlpha = 1;
      continue;
    }
    ctx.fillStyle = age < state.config.smokeTtl / 3 ? COLOR.smokeFresh : COLOR.smoke;
    ctx.fillRect(cx, cy, TILE_PX, TILE_PX);
  }
}

function drawEnemies(ctx: CanvasRenderingContext2D, state: GameState, vp: Viewport, sprites?: SpriteSheet): void {
  for (const enemy of state.enemies) {
    const cx = subpixelToPixel(enemy.car.x) - vp.x;
    const cy = subpixelToPixel(enemy.car.y) - vp.y;
    if (cx < -TILE_PX || cy < -TILE_PX || cx > vp.width + TILE_PX || cy > vp.height + TILE_PX) continue;
    const stunned = state.tick < enemy.stunUntil;
    if (sprites) {
      drawSprite(ctx, sprites, carSpriteName('enemy', enemy.car.dir), cx - TILE_PX / 2, cy - TILE_PX / 2);
      if (stunned) {
        ctx.fillStyle = 'rgba(108, 112, 134, 0.45)';
        ctx.fillRect(cx - TILE_PX / 2, cy - TILE_PX / 2, TILE_PX, TILE_PX);
        ctx.fillStyle = '#fab387';
        ctx.fillRect(cx - 1, cy - TILE_PX / 2 - 3, 2, 2);
      }
      continue;
    }
    const size = TILE_PX - 4;
    const half = size / 2;
    ctx.fillStyle = stunned ? COLOR.enemyStunned : COLOR.enemy;
    ctx.fillRect(cx - half, cy - half, size, size);
    ctx.strokeStyle = stunned ? COLOR.enemyStunned : COLOR.enemyOutline;
    ctx.lineWidth = 1;
    ctx.strokeRect(cx - half + 0.5, cy - half + 0.5, size - 1, size - 1);
    if (stunned) {
      // Wobbly dazed marker
      ctx.fillStyle = '#fab387';
      ctx.fillRect(cx - 1, cy - half - 3, 2, 2);
    }
  }
}

function drawMaze(ctx: CanvasRenderingContext2D, maze: Maze, vp: Viewport, sprites?: SpriteSheet): void {
  const startTx = Math.max(0, Math.floor(vp.x / TILE_PX));
  const startTy = Math.max(0, Math.floor(vp.y / TILE_PX));
  const endTx = Math.min(maze.width, Math.ceil((vp.x + vp.width) / TILE_PX));
  const endTy = Math.min(maze.height, Math.ceil((vp.y + vp.height) / TILE_PX));

  for (let ty = startTy; ty < endTy; ty++) {
    for (let tx = startTx; tx < endTx; tx++) {
      const screenX = tileToPixel(tx) - vp.x;
      const screenY = tileToPixel(ty) - vp.y;
      const kind = maze.tiles[ty * maze.width + tx];
      if (sprites) {
        drawSprite(ctx, sprites, kind === TILE.WALL ? 'grass' : 'street', screenX, screenY);
        continue;
      }
      if (kind === TILE.WALL) {
        ctx.fillStyle = COLOR.wall;
        ctx.fillRect(screenX, screenY, TILE_PX, TILE_PX);
        ctx.fillStyle = COLOR.wallEdge;
        ctx.fillRect(screenX, screenY, TILE_PX, 1);
        ctx.fillRect(screenX, screenY, 1, TILE_PX);
      } else {
        ctx.fillStyle = COLOR.corridor;
        ctx.fillRect(screenX, screenY, TILE_PX, TILE_PX);
        ctx.fillStyle = COLOR.corridorGrid;
        ctx.fillRect(screenX + TILE_PX / 2, screenY + TILE_PX / 2, 1, 1);
      }
    }
  }
}

function drawFlags(ctx: CanvasRenderingContext2D, state: GameState, vp: Viewport, sprites?: SpriteSheet): void {
  for (const flag of state.flags) {
    if (flag.collected) continue;
    const x = tileToPixel(flag.tile.x) - vp.x;
    const y = tileToPixel(flag.tile.y) - vp.y;
    const cx = x + TILE_PX / 2;
    const cy = y + TILE_PX / 2;
    if (cx < -TILE_PX || cy < -TILE_PX || cx > vp.width + TILE_PX || cy > vp.height + TILE_PX) continue;
    const colors = flagColors(flag.kind);
    if (sprites) {
      drawSprite(ctx, sprites, 'flag', x, y);
      if (flag.kind !== 'yellow') {
        // Powerup overlay: a small color-coded diamond marker.
        ctx.fillStyle = colors.pennant;
        ctx.beginPath();
        ctx.moveTo(x + 24, y + 4);
        ctx.lineTo(x + 29, y + 9);
        ctx.lineTo(x + 24, y + 14);
        ctx.lineTo(x + 19, y + 9);
        ctx.closePath();
        ctx.fill();
      }
      continue;
    }
    // Post
    ctx.fillStyle = colors.post;
    ctx.fillRect(cx - 1, cy - TILE_PX / 2 + 2, 1, TILE_PX - 4);
    // Pennant
    ctx.fillStyle = colors.pennant;
    ctx.beginPath();
    ctx.moveTo(cx, cy - TILE_PX / 2 + 2);
    ctx.lineTo(cx + 6, cy - TILE_PX / 2 + 5);
    ctx.lineTo(cx, cy - TILE_PX / 2 + 8);
    ctx.closePath();
    ctx.fill();
    if (colors.glyph) {
      ctx.fillStyle = COLOR.bg;
      ctx.font = 'bold 6px ui-monospace, Menlo, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(colors.glyph, cx + 2, cy - TILE_PX / 2 + 5);
    }
  }
}

function drawPlayer(ctx: CanvasRenderingContext2D, state: GameState, vp: Viewport, sprites?: SpriteSheet): void {
  const cx = subpixelToPixel(state.player.x) - vp.x;
  const cy = subpixelToPixel(state.player.y) - vp.y;
  if (sprites) {
    drawSprite(ctx, sprites, carSpriteName('player', state.player.dir), cx - TILE_PX / 2, cy - TILE_PX / 2);
    return;
  }
  const size = TILE_PX - 4;
  const half = size / 2;
  ctx.fillStyle = COLOR.player;
  ctx.fillRect(cx - half, cy - half, size, size);
  ctx.strokeStyle = COLOR.playerOutline;
  ctx.lineWidth = 1;
  ctx.strokeRect(cx - half + 0.5, cy - half + 0.5, size - 1, size - 1);
  // Heading tick mark
  if (state.player.dir !== null) {
    ctx.fillStyle = COLOR.playerOutline;
    const tick = 3;
    switch (state.player.dir) {
      case 'N': ctx.fillRect(cx - 1, cy - half - tick, 2, tick); break;
      case 'S': ctx.fillRect(cx - 1, cy + half, 2, tick); break;
      case 'E': ctx.fillRect(cx + half, cy - 1, tick, 2); break;
      case 'W': ctx.fillRect(cx - half - tick, cy - 1, tick, 2); break;
    }
  }
}

function carSpriteName(owner: 'player' | 'enemy', dir: GameState['player']['dir']): SpriteName {
  switch (dir) {
    case 'E': return `${owner}_car_e`;
    case 'S': return `${owner}_car_s`;
    case 'W': return `${owner}_car_w`;
    case 'N':
    case null:
      return `${owner}_car_n`;
  }
}
